import React from 'react';
import { CompactHeader } from '../../ui/CompactHeader';
import { SummaryCard } from '../../ui/SummaryCard';
import { StatusBadge } from '../../ui/StatusBadge';
import {
  UsersIcon,
  ApiKeyIcon,
  ProjectIcon,
  BackIcon,
  CheckIcon
} from '../../ui/Icons';
import { UI_CONSTANTS } from '../../../constants/ui';
import { formatDate, isActive } from '../../../utils/dateUtils';
import { ProjectDetailViewProps } from '../types';

export const ProjectDetailView: React.FC<ProjectDetailViewProps> = React.memo(({ selectedProject, projectDetail, onBack }) => {
  const projectActions = [
    <button
      key="back"
      onClick={onBack}
      className="px-3 py-1.5 text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-all flex items-center gap-1.5"
    >
      <BackIcon className="w-3.5 h-3.5" />
      Back to Overview
    </button>
  ];

  return (
    <div className="space-y-3">
      <CompactHeader
        title={selectedProject.name}
        subtitle={`Created ${formatDate(selectedProject.created_at)}`}
        icon={<ProjectIcon />}
        actions={projectActions}
      />

      {/* Project Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4">
        <SummaryCard
          title="Users"
          value={projectDetail.users.length}
          icon={<UsersIcon />}
          variant="slate"
        />
        <SummaryCard
          title="API Keys"
          value={projectDetail.apiKeys.length}
          icon={<ApiKeyIcon />}
          variant="amber"
        />
        <SummaryCard
          title="Active Keys"
          value={projectDetail.apiKeys.filter(key => isActive(key.last_used_at, UI_CONSTANTS.ACTIVE_THRESHOLD_DAYS)).length}
          icon={<CheckIcon />}
          variant="emerald"
        />
      </div>

      {/* Project Users */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-2 sm:px-4 py-1.5 sm:py-2 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white">Project Users</h2>
        </div>
        <div className="overflow-x-auto custom-scrollbar">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-2 sm:px-3 py-1 sm:py-1.5 text-left text-[10px] sm:text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Name
                </th>
                <th className="hidden sm:table-cell px-2 sm:px-3 py-1 sm:py-1.5 text-left text-[10px] sm:text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-2 sm:px-3 py-1 sm:py-1.5 text-left text-[10px] sm:text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Role
                </th>
                <th className="hidden sm:table-cell px-2 sm:px-3 py-1 sm:py-1.5 text-left text-[10px] sm:text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Added Date
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {projectDetail.users.map((user, idx) => (
                <tr key={user.id} className={idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-900'}>
                  <td className="px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs font-medium text-gray-900 dark:text-white">
                    {user.name}
                  </td>
                  <td className="hidden sm:table-cell px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                    {user.email}
                  </td>
                  <td className="px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                    <StatusBadge
                      status={user.role}
                      variant={user.role === 'owner' ? 'amber' : 'slate'}
                    />
                  </td>
                  <td className="hidden sm:table-cell px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                    {formatDate(user.added_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* API Keys */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-2 sm:px-4 py-1.5 sm:py-2 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white">API Keys</h2>
        </div>
        <div className="overflow-x-auto custom-scrollbar">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-2 sm:px-3 py-1 sm:py-1.5 text-left text-[10px] sm:text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-2 sm:px-3 py-1 sm:py-1.5 text-left text-[10px] sm:text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Owner
                </th>
                <th className="hidden sm:table-cell px-2 sm:px-3 py-1 sm:py-1.5 text-left text-[10px] sm:text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-2 sm:px-3 py-1 sm:py-1.5 text-left text-[10px] sm:text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Last Used
                </th>
                <th className="px-2 sm:px-3 py-1 sm:py-1.5 text-left text-[10px] sm:text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {projectDetail.apiKeys.map((apiKey, idx) => {
                const isActiveKey = isActive(apiKey.last_used_at, UI_CONSTANTS.ACTIVE_THRESHOLD_DAYS);

                return (
                  <tr key={apiKey.id} className={idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-900'}>
                    <td className="px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs font-medium text-gray-900 dark:text-white">
                      {apiKey.name}
                    </td>
                    <td className="px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                      {apiKey.owner?.type === 'user' ? apiKey.owner.user?.name :
                       apiKey.owner?.type === 'service_account' ? apiKey.owner.service_account?.name :
                       'Unknown'}
                    </td>
                    <td className="hidden sm:table-cell px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                      {formatDate(apiKey.created_at)}
                    </td>
                    <td className="px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                      {apiKey.last_used_at ? formatDate(apiKey.last_used_at) : 'Never'}
                    </td>
                    <td className="px-2 sm:px-3 py-1 sm:py-1.5 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                      <StatusBadge
                        status={isActiveKey ? 'Active' : 'Inactive'}
                        variant={isActiveKey ? 'emerald' : 'slate'}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
});
ProjectDetailView.displayName = 'ProjectDetailView';
